import { createScopedLogger, ScopedLogger } from '@/utils/logging/createScopedLogger';

const defaultLogger = createScopedLogger('Supabase');

export interface SupabaseLikeResult<T> {
  data: T | null;
  error: { message?: string; code?: string; details?: string } | null;
}

export interface WithSupabaseResultOptions<T> {
  operation: string;
  logger?: ScopedLogger;
  fallback?: T;
  throwOnError?: boolean;
  logNullAsInfo?: boolean;
}

export interface SupabaseOutcome<T> {
  data: T | null;
  error: Error | null;
  ok: boolean;
}

function toError(raw: SupabaseLikeResult<unknown>['error'], operation: string): Error {
  if (!raw) return new Error(`${operation} failed`);
  const parts: string[] = [];
  if (raw.code) parts.push(`code=${raw.code}`);
  if (raw.message) parts.push(raw.message);
  if (raw.details) parts.push(raw.details);
  const msg = parts.length > 0 ? parts.join(' | ') : `${operation} failed`;
  const err = new Error(msg);
  (err as Error & { code?: string }).code = raw.code;
  return err;
}

export async function withSupabaseResult<T>(
  promise: PromiseLike<SupabaseLikeResult<T>>,
  options: WithSupabaseResultOptions<T>
): Promise<SupabaseOutcome<T>> {
  const { operation, logger = defaultLogger, fallback, throwOnError = false, logNullAsInfo = false } = options;

  try {
    const { data, error } = await promise;

    if (error) {
      const err = toError(error, operation);
      logger.error(`${operation} error:`, err.message);
      if (throwOnError) throw err;
      return { data: fallback ?? null, error: err, ok: false };
    }

    if (data === null || data === undefined) {
      if (logNullAsInfo) logger.info(`${operation} returned no data`);
      return { data: fallback ?? null, error: null, ok: true };
    }

    return { data, error: null, ok: true };
  } catch (caught) {
    const err = caught instanceof Error ? caught : new Error(String(caught));
    logger.error(`${operation} threw:`, err.message);
    if (throwOnError) throw err;
    return { data: fallback ?? null, error: err, ok: false };
  }
}

export async function unwrapSupabaseResult<T>(
  promise: PromiseLike<SupabaseLikeResult<T>>,
  options: WithSupabaseResultOptions<T>
): Promise<T | null> {
  const { data } = await withSupabaseResult(promise, options);
  return data;
}
